/**
 * Lazy Paddle.js (v2) loader for the supporter checkout (#666).
 *
 * The script is only injected the first time a user opens checkout, and only
 * when the backend reports billing as enabled — so nothing loads while billing
 * is inert. Selling is web-only to avoid the Apple/Google 30% IAP cut.
 */

const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

export interface PaddleCheckoutItem {
  priceId: string;
  quantity: number;
}

export interface PaddleCheckoutOptions {
  items: PaddleCheckoutItem[];
  customData?: Record<string, unknown>;
  customer?: { email: string };
  settings?: Record<string, unknown>;
}

export interface PaddleEvent {
  name?: string;
  [key: string]: unknown;
}

interface PaddleGlobal {
  Environment: { set: (env: string) => void };
  Initialize: (options: {
    token: string;
    eventCallback?: (event: PaddleEvent) => void;
  }) => void;
  Checkout: { open: (options: PaddleCheckoutOptions) => void };
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

// Reused across opens so the script + Initialize run at most once.
let readyPromise: Promise<PaddleGlobal> | null = null;

// The active event handler; swapped by the component before each open so the
// single Paddle.Initialize callback can dispatch to current React state.
let eventHandler: ((event: PaddleEvent) => void) | null = null;

export function setPaddleEventHandler(
  handler: ((event: PaddleEvent) => void) | null
): void {
  eventHandler = handler;
}

function injectScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Paddle) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = PADDLE_JS_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Paddle.js'));
    document.head.appendChild(script);
  });
}

/**
 * Ensure Paddle.js is loaded and initialised, returning the global. Safe to
 * call repeatedly — the load + Initialize happen exactly once.
 */
export function loadPaddle(
  clientToken: string,
  environment: string
): Promise<PaddleGlobal> {
  if (readyPromise) {
    return readyPromise;
  }
  readyPromise = injectScript()
    .then(() => {
      const paddle = window.Paddle;
      if (!paddle) {
        throw new Error('Paddle.js did not initialise');
      }
      // Only sandbox needs an explicit environment; production is the default.
      if (environment === 'sandbox') {
        paddle.Environment.set('sandbox');
      }
      paddle.Initialize({
        token: clientToken,
        eventCallback: (event) => eventHandler?.(event),
      });
      return paddle;
    })
    .catch((err) => {
      // Don't cache the failure — a later click should be able to retry the
      // script load instead of being permanently broken until a page refresh.
      readyPromise = null;
      throw err;
    });
  return readyPromise;
}
